import { useId, useState, type FormEvent } from "react";
import { z } from "zod";
import type { MachineTarget, MachineView } from "../../shared/contracts";
import { CADENCE_OPTIONS, cadenceLabel, cadenceValue } from "../lib/format";
import { Button, Field, Input, Notice, Select, Textarea } from "./ui";

export interface MissionFormValues {
  title: string;
  goal: string;
  cadenceMinutes: number | null;
  machine: MachineTarget;
  runNow: boolean;
}

const schema = z.object({
  title: z.string().trim().min(1, "Donne un titre à la mission.").max(120, "120 caractères maximum."),
  goal: z.string().trim().min(1, "Décris l'objectif.").max(4000, "4 000 caractères maximum."),
});

/** Options of the "Machine" select: none, the cloud computer, each paired local computer. */
export function machineOptions(machines: MachineView[] | undefined): { value: MachineTarget; label: string }[] {
  const hasCloud = machines?.some((m) => m.kind === "cloud") ?? false;
  return [
    { value: "none", label: "Aucun" },
    { value: "cloud", label: hasCloud ? "Mon ordinateur cloud" : "Mon ordinateur cloud (créé au besoin)" },
    ...(machines ?? []).filter((m) => m.kind === "local").map((m) => ({ value: `local:${m.id}` as MachineTarget, label: `Ordinateur local ${m.name}` })),
  ];
}

/** Title / goal / cadence / machine. Used to create a mission and to edit one. */
export function MissionForm({
  initial,
  machines,
  submitLabel,
  showRunNow = false,
  onSubmit,
  onCancel,
}: {
  initial: MissionFormValues;
  machines: MachineView[] | undefined;
  submitLabel: string;
  showRunNow?: boolean;
  onSubmit: (values: MissionFormValues) => Promise<void>;
  onCancel?: () => void;
}) {
  const [title, setTitle] = useState(initial.title);
  const [goal, setGoal] = useState(initial.goal);
  const [cadence, setCadence] = useState(cadenceValue(initial.cadenceMinutes));
  const [machine, setMachine] = useState<string>(initial.machine);
  const [runNow, setRunNow] = useState(initial.runNow);
  const [errors, setErrors] = useState<{ title?: string; goal?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ids = { title: useId(), goal: useId(), cadence: useId(), machine: useId(), runNow: useId() };

  const options = machineOptions(machines);
  if (!options.some((o) => o.value === machine)) options.push({ value: machine as MachineTarget, label: "Ordinateur retiré" });
  const customCadence = cadence === "custom" ? initial.cadenceMinutes : null;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const parsed = schema.safeParse({ title, goal });
    if (!parsed.success) {
      const next: { title?: string; goal?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if ((key === "title" || key === "goal") && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setError(null);
    setBusy(true);
    try {
      const minutes = cadence === "custom" ? customCadence : (CADENCE_OPTIONS.find((o) => o.value === cadence)?.minutes ?? null);
      await onSubmit({ title: parsed.data.title, goal: parsed.data.goal, cadenceMinutes: minutes, machine: machine as MachineTarget, runNow });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible d'enregistrer la mission.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <Field label="Titre" htmlFor={ids.title} error={errors.title}>
        <Input id={ids.title} value={title} maxLength={120} placeholder="Ex. Veille vols Paris → Tokyo" onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="Objectif" htmlFor={ids.goal} error={errors.goal} hint="Ce qu'il doit obtenir, et quand te prévenir.">
        <Textarea
          id={ids.goal}
          value={goal}
          maxLength={4000}
          placeholder="Ex. Surveille le prix des vols Paris → Tokyo en mars et préviens-moi sous 700 €."
          onChange={(e) => setGoal(e.target.value)}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Fréquence" htmlFor={ids.cadence}>
          <Select id={ids.cadence} value={cadence} onChange={(e) => setCadence(e.target.value)}>
            {CADENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {cadence === "custom" ? <option value="custom">{cadenceLabel(initial.cadenceMinutes)}</option> : null}
          </Select>
        </Field>
        <Field label="Machine" htmlFor={ids.machine} hint="Sur quel ordinateur il travaille (aucun : recherche et apps seulement).">
          <Select id={ids.machine} value={machine} onChange={(e) => setMachine(e.target.value)}>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {showRunNow ? (
        <label htmlFor={ids.runNow} className="flex items-center gap-2 text-[14px]">
          <input id={ids.runNow} type="checkbox" className="h-4 w-4" checked={runNow} onChange={(e) => setRunNow(e.target.checked)} />
          Lancer tout de suite
        </label>
      ) : null}
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" busy={busy}>
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Fermer
          </Button>
        ) : null}
      </div>
    </form>
  );
}
